import APIService from "./APIService";

/**
 * API service for the Platform Admin weekly blocklist review.
 * Surfaces engine-proposed-bad domains and user "actually safe" corrections,
 * and applies the admin's confirm / reject / clear decisions.
 * Requires platform_admin role.
 */
class AdminBlocklistAPI {
  static BASE_PATH = "/api/v1/admin/blocklist";

  /**
   * Load the current review batch.
   * @returns {Promise<Object>} { proposedBad, falsePositives, activeUserCount, threshold, blocklistSize }
   */
  static async getReview() {
    try {
      return await APIService.get(`${AdminBlocklistAPI.BASE_PATH}/review`);
    } catch (error) {
      console.error("AdminBlocklistAPI: Error fetching review:", error);
      throw error;
    }
  }

  /**
   * Paginated listing of every blocklist entry, with optional filters.
   * @param {Object} params { page, size, active, source, search }
   * @returns {Promise<Object>} { entries, totalElements, totalPages, currentPage, blocklistSize }
   */
  static async getBlocklistEntries({
    page = 0,
    size = 25,
    active,
    source,
    search,
  } = {}) {
    const query = AdminBlocklistAPI._buildQuery({
      page,
      size,
      active,
      source,
      search,
    });
    try {
      return await APIService.get(
        `${AdminBlocklistAPI.BASE_PATH}/entries${query}`,
      );
    } catch (error) {
      console.error("AdminBlocklistAPI: Error fetching entries:", error);
      throw error;
    }
  }

  /**
   * Paginated listing of reported URLs, with optional filters.
   * @param {Object} params { page, size, status, reportType, search }
   * @returns {Promise<Object>} { reports, totalElements, totalPages, currentPage }
   */
  static async getReports({
    page = 0,
    size = 25,
    status,
    reportType,
    search,
  } = {}) {
    const query = AdminBlocklistAPI._buildQuery({
      page,
      size,
      status,
      reportType,
      search,
    });
    try {
      return await APIService.get(`/api/v1/admin/url-reports${query}`);
    } catch (error) {
      console.error("AdminBlocklistAPI: Error fetching url reports:", error);
      throw error;
    }
  }

  /** Confirm a domain onto the blocklist. */
  static async confirm(domain, notes) {
    return AdminBlocklistAPI._action("confirm", domain, notes);
  }

  /** Reject a proposed-bad domain (do not block). */
  static async reject(domain, notes) {
    return AdminBlocklistAPI._action("reject", domain, notes);
  }

  /** Accept a user "actually safe" correction (clear a confirmed false positive). */
  static async clearFalsePositive(domain, notes) {
    return AdminBlocklistAPI._action("clear-false-positive", domain, notes);
  }

  /** Run the weekly batch on demand. */
  static async runBatch() {
    try {
      return await APIService.post(
        `${AdminBlocklistAPI.BASE_PATH}/run-batch`,
        {},
      );
    } catch (error) {
      console.error("AdminBlocklistAPI: Error running batch:", error);
      throw error;
    }
  }

  static async _action(action, domain, notes) {
    try {
      return await APIService.post(`${AdminBlocklistAPI.BASE_PATH}/${action}`, {
        domain,
        notes,
      });
    } catch (error) {
      console.error(`AdminBlocklistAPI: Error on ${action}:`, error);
      throw error;
    }
  }

  /** Build a `?a=b&c=d` query string, skipping null/undefined/blank values. */
  static _buildQuery(params) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") return;
      search.append(key, value);
    });
    const qs = search.toString();
    return qs ? `?${qs}` : "";
  }
}

export default AdminBlocklistAPI;
